import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import nestGet from '@tmap/mmm-core/nestGet';
import doesProfileMatchCriteria from '@tmap/mmm-core/commonApp/doesProfileMatchCriteria';
import { fromKey } from '@tmap/mmm-core/entityKey';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Divider } from '@tmap/mmm-style-guide/src/Divider';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { CircularProgress } from '@tmap/mmm-style-guide/src/Progress';
import {
  ELIGIBILITY,
  EMPLOYMENT,
  HOUSEHOLD,
  MOVE_DETAILS,
  PERSONAL_INFO,
  MORE_DETAILS,
} from '@tmap/mmm-core/commonApp/funnelCategories';
import AutoSaveForm from '@tmap/mmm-style-guide/src/AutoSaveForm';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { diff } from '@tmap/mmm-core/deep-object-diff';
import currency from 'currency.js';

import requireUser from '../../lib/requireUser';
import BigCard from '../../layouts/bigCard';
import ProgressBar from './progressBar';
import { getIncentives } from '../../actions/incentive';
import CommonAppHeadline from './headline';
import CommonAppSubHead from './subhead';
import { updateUserProfile } from '../../actions/userProfile';
import { sendThankYou, updateUserFavorite } from '../../actions/user';
import EligibilityFormBody from './eligibilityForm';
import { getCommonAppConfig } from '../../actions/commonApp';
import ContactInfoFormBody from './contactInfoForm';
import { getNextStage, getStageInfo } from '../../lib/commonApp/stageOrder';
import MoveDetailsFormBody from './moveDetailsForm';
import HouseholdFormBody from './householdForm';
import EmploymentFormBody from './employmentForm';
import MoreDetailsFormBody from './moreDetailsForm';
import checkEligibility from '../../lib/commonApp/checkEligibility';

import analytics from '../../lib/analytics';

const getStepComponent = (stage) => {
  switch (stage) {
    case ELIGIBILITY:
      return EligibilityFormBody;
    case PERSONAL_INFO:
      return ContactInfoFormBody;
    case MOVE_DETAILS:
      return MoveDetailsFormBody;
    case HOUSEHOLD:
      return HouseholdFormBody;
    case EMPLOYMENT:
      return EmploymentFormBody;
    case MORE_DETAILS:
      return MoreDetailsFormBody;
    default:
      return null;
  }
};

function CommonApp(props) {
  const {
    stage = ELIGIBILITY,
  } = props;
  const router = useRouter();
  const fieldConfigs = useSelector((state) => state.commonApp?.config, shallowEqual);
  const userProfile = useSelector((state) => state.users?.current, shallowEqual);
  const userListItem = useSelector((state) => nestGet(state, ['users', 'favoriteListItems', router.query.itemId]), shallowEqual);
  const incentive = useSelector((state) => nestGet(state, ['incentives', 'incentives', userListItem ? fromKey(userListItem.itemKey).id : undefined]));
  const app = fieldConfigs?.app;
  const dispatch = useDispatch();

  const initialValues = {
    user_profile: userProfile,
    user_list_item: userListItem,
  };

  useEffect(() => {
    if (!incentive && userListItem?.itemKey) {
      dispatch(getIncentives([userListItem.itemKey]));
    }
  }, [dispatch, incentive, userListItem?.itemKey]);

  useEffect(() => {
    if (incentive) {
      dispatch(getCommonAppConfig(incentive));
    }
  }, [dispatch, incentive]);

  const [loadingContinue, setLoadingContinue] = useState(false);
  const [loadingBack, setLoadingBack] = useState(false);
  const disableForm = loadingBack || loadingContinue;
  const onFormChange = (values) => {
    if (disableForm) return;
    const { user_profile: updatedUserProfile } = values;
    let updatedListItem = { ...values.user_list_item };
    if (app?._id) {
      updatedListItem = {
        ...updatedListItem,
        app: {
          ...updatedListItem.app,
          applicationId: app._id,
        },
      };
    }
    const profileUpdate = diff(userProfile, updatedUserProfile);
    const listItemUpdate = diff(userListItem, updatedListItem);

    if (Object.keys(profileUpdate).length > 0) {
      dispatch(updateUserProfile(profileUpdate));
    }
    if (Object.keys(listItemUpdate).length > 0) {
      dispatch(updateUserFavorite({
        ...listItemUpdate,
        itemKey: userListItem.itemKey,
      }));
    }
  };

  useEffect(() => {
    setLoadingBack(false);
    setLoadingContinue(false);
  }, []);

  const qs = `?itemId=${router.query.itemId}`;
  const nextStage = getNextStage(stage);
  const nextStageUrl = `/apply/${getStageInfo(nextStage)?.url}${qs}`;

  const handleCancel = () => {
    setLoadingBack(true);
    if (incentive.slug.current) {
      router.push(`/get-paid/${incentive.slug.current}`);
    } else {
      router.push('/myprofile');
    }
  };

  const handleBack = () => {
    setLoadingBack(true);
    router.back();
  };

  const handleContinue = (values) => () => {
    if (loadingContinue) return;
    setLoadingContinue(true);

    if (stage === ELIGIBILITY) {
      if (!checkEligibility(fieldConfigs.data, values)) {
        // If not passing eligibility, route to rejection
        analytics.track('rejected', {
          entityKey: userListItem.itemKey,
        });
        router.push(`/apply/notEligible${qs}`);
        return;
      }
      if (!userListItem.app?.started) {
        dispatch(updateUserFavorite({
          app: {
            started: (new Date()).toJSON(),
          },
          itemKey: userListItem.itemKey,
        }));
        // If pass, set the app start time
      }
    }
    if (stage === MORE_DETAILS) {
      dispatch(updateUserFavorite({
        app: {
          completed: (new Date()).toJSON(),
        },
        status: 'applied',
        itemKey: userListItem.itemKey,
      }));
      dispatch(sendThankYou(userListItem._id));
      // set the app completion time
      analytics.track('applied', {
        entityKey: userListItem.itemKey,
        workingRemote: userProfile.workingRemote,
        income: currency(userProfile?.employment?.income || '0').value,
        qualifies: doesProfileMatchCriteria(userProfile, app).overall,
        customerType: incentive.customerType,
      });
      router.push(`/apply/housing?from=${incentive.slug.current}`);
      return;
    }
    if (userListItem.app?.progress !== stage) {
      dispatch(updateUserFavorite({ app: { progress: stage }, itemKey: userListItem.itemKey }));
      // update the latest stage in the list item if not already set
    }
    router.push(nextStageUrl);
  };

  const ready = (fieldConfigs.isFetching === false);

  return (
    <BigCard>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ProgressBar stage={stage} incentive={incentive} />
        </Grid>
        <Grid item xs={12} text='center'>
          <CommonAppHeadline stage={stage} incentive={incentive} />
        </Grid>
        <Grid item xs={12} text='center' sx={{ marginLeft: '10%', marginRight: '10%' }}>
          <CommonAppSubHead stage={stage} incentive={incentive} />
        </Grid>
        {
          stage !== ELIGIBILITY && <Grid item xs={12} sx={{ marginTop: '20px' }}><Divider /></Grid>
        }
        <Grid item xs={12} mt={4}>
          {ready ? (
            <AutoSaveForm
              initialValues={initialValues}
              onFormChange={onFormChange}
            >{({ values, hasValidationErrors }) => {
              const StageComponent = getStepComponent(stage);
              return (
                <>
                  <StageComponent
                    stage={stage}
                    values={values}
                    incentive={incentive}
                    fieldConfigs={fieldConfigs.data}
                  />
                  <Grid container mt={5} justifyContent='space-between'>
                    <Grid item>
                      <Grid
                        container
                        flexWrap='nowrap'
                        alignItems='center'
                        justifyContent='center'
                      >
                        <Grid item>
                          {stage === ELIGIBILITY
                            ? (
                              <Button
                                disabled={disableForm}
                                onClick={handleCancel}
                              >
                                Cancel
                              </Button>
                            ) : (
                              <Button
                                disabled={disableForm}
                                onClick={handleBack}
                              >
                                Back
                              </Button>
                            )}
                        </Grid>
                        {loadingBack && (
                          <Grid item display='flex'>
                            <CircularProgress size={14} disableShrink />
                          </Grid>
                        )}

                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid
                        container
                        flexWrap='nowrap'
                        alignItems='center'
                        justifyContent='center'
                        columnSpacing={1.5}
                      >
                        {loadingContinue && (
                          <Grid item display='flex'>
                            <CircularProgress size={14} disableShrink />
                          </Grid>
                        )}
                        <Grid item>
                          <Button
                            variant='contained'
                            color='convert'
                            disabled={disableForm || hasValidationErrors}
                            onClick={handleContinue(values)}
                          >
                            {stage === MORE_DETAILS ? 'Submit' : 'Continue'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              );
            }}
            </AutoSaveForm>
          ) : (
            <Box height='120vh' display='flex' justifyContent='center'>
              <CircularProgress size={40} disableShrink />
            </Box>
          )}
        </Grid>
      </Grid>
    </BigCard>
  );
}

export default requireUser(CommonApp);
